import { For, Show, createEffect, createMemo, createSignal, createUniqueId, onMount } from "solid-js";
import type { FriendCoordinateOption, FriendCoordinateReference } from "./types";
import { styles } from "../../styles";

const MAX_VISIBLE_COORDINATE_OPTIONS = 100;

export function coordinateOptionValue(reference: FriendCoordinateReference): string {
  return reference ? `${reference.type}:${reference.id}` : "";
}

export function parseCoordinateOptionValue(value: string): FriendCoordinateReference {
  if (!value) return null;
  const separator = value.indexOf(":");
  if (separator < 0) return null;
  const type = value.slice(0, separator);
  const id = value.slice(separator + 1);
  return (type === "existing" || type === "mixed") && id ? { type, id } : null;
}

export function FriendCoordinateSelect(props: {
  options: FriendCoordinateOption[];
  value: FriendCoordinateReference;
  disabled?: boolean;
  autofocus?: boolean;
  onChange: (value: FriendCoordinateReference) => void;
}) {
  let input: HTMLInputElement | undefined;
  const listboxId = `friend-coordinate-options-${createUniqueId()}`;
  const [query, setQuery] = createSignal("");
  const [open, setOpen] = createSignal(false);
  const [editing, setEditing] = createSignal(false);
  const [activeIndex, setActiveIndex] = createSignal(0);
  const selectedLabel = () => {
    const value = coordinateOptionValue(props.value);
    return value
      ? props.options.find((option) => coordinateOptionValue(option) === value)?.name ?? ""
      : "";
  };
  const indexedOptions = createMemo(() => props.options.map((option) => ({
    key: coordinateOptionValue(option),
    label: option.name,
    group: option.type === "mixed" ? "ミックス" : "既存",
    value: { type: option.type, id: option.id } as FriendCoordinateReference,
    searchText: `${option.name} ${option.id}`.normalize("NFKC").toLocaleLowerCase("ja"),
  })));
  const normalizedQuery = createMemo(() => editing()
    ? query().trim().normalize("NFKC").toLocaleLowerCase("ja")
    : "");
  const matchingOptions = createMemo(() => {
    const search = normalizedQuery();
    return [
      ...(!search || "コーデ未設定".includes(query().trim())
        ? [{ key: "unset", label: "コーデ未設定", group: "未設定", value: null as FriendCoordinateReference }]
        : []),
      ...indexedOptions().filter((option) => !search || option.searchText.includes(search)),
    ];
  });
  const visibleOptions = createMemo(() => matchingOptions().slice(0, MAX_VISIBLE_COORDINATE_OPTIONS));

  createEffect(() => {
    const label = selectedLabel();
    if (!open()) setQuery(label);
  });

  onMount(() => {
    if (props.autofocus) input?.focus({ preventScroll: true });
  });

  const selectOption = (index: number) => {
    const option = visibleOptions()[index];
    if (!option) return;
    props.onChange(option.value);
    setQuery(option.value ? option.label : "");
    setEditing(false);
    setOpen(false);
  };
  const moveActive = (offset: number) => {
    const length = visibleOptions().length;
    if (!length) return;
    const next = (activeIndex() + offset + length) % length;
    setActiveIndex(next);
    queueMicrotask(() => document.getElementById(`${listboxId}-${next}`)?.scrollIntoView({ block: "nearest" }));
  };

  return (
    <div class={styles.friendCoordinateCombobox}>
      <input
        ref={input}
        class={styles.input}
        type="text"
        role="combobox"
        value={query()}
        placeholder="コーデ名を入力して検索"
        autocomplete="off"
        autofocus={props.autofocus}
        disabled={props.disabled}
        aria-label="コーデを検索して選択"
        aria-autocomplete="list"
        aria-expanded={open()}
        aria-controls={listboxId}
        aria-activedescendant={open() && visibleOptions()[activeIndex()] ? `${listboxId}-${activeIndex()}` : undefined}
        onFocus={() => {
          setEditing(false);
          setActiveIndex(0);
          setOpen(true);
        }}
        onInput={(event) => {
          setQuery(event.currentTarget.value);
          setEditing(true);
          setActiveIndex(0);
          setOpen(true);
        }}
        onBlur={() => {
          setOpen(false);
          setEditing(false);
          setQuery(selectedLabel());
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown") {
            event.preventDefault();
            if (!open()) setOpen(true);
            else moveActive(1);
          } else if (event.key === "ArrowUp") {
            event.preventDefault();
            if (!open()) setOpen(true);
            else moveActive(-1);
          } else if (event.key === "Enter" && open()) {
            event.preventDefault();
            selectOption(activeIndex());
          } else if (event.key === "Escape" && open()) {
            event.preventDefault();
            setOpen(false);
            setEditing(false);
            setQuery(selectedLabel());
          }
        }}
      />
      <Show when={query() && !props.disabled}>
        <button
          class={styles.friendCoordinateClear}
          type="button"
          title="コーデ名検索を解除"
          aria-label="コーデ名検索を解除"
          onPointerDown={(event) => event.preventDefault()}
          onClick={() => {
            setQuery("");
            setEditing(true);
            setActiveIndex(0);
            setOpen(true);
            input?.focus();
          }}
        >✕</button>
      </Show>
      <Show when={open()}>
        <div id={listboxId} class={styles.friendCoordinateOptions} role="listbox" aria-label="コーデ候補">
          <Show when={matchingOptions().length > visibleOptions().length}>
            <p class={styles.friendCoordinateResultSummary}>
              {matchingOptions().length.toLocaleString()}件中、先頭{visibleOptions().length}件を表示しています。文字を入力して絞り込んでください。
            </p>
          </Show>
          <For each={visibleOptions()}>{(option, index) => (
            <button
              id={`${listboxId}-${index()}`}
              class={styles.friendCoordinateOption}
              classList={{ [styles.friendCoordinateOptionActive]: activeIndex() === index() }}
              type="button"
              role="option"
              aria-selected={coordinateOptionValue(props.value) === coordinateOptionValue(option.value)}
              onPointerMove={() => setActiveIndex(index())}
              onPointerDown={(event) => event.preventDefault()}
              onClick={() => selectOption(index())}
            >
              <span>{option.label}</span>
              <small>{option.group}</small>
            </button>
          )}</For>
          <Show when={visibleOptions().length === 0}>
            <p class={styles.friendCoordinateEmpty}>一致するコーデがありません。</p>
          </Show>
        </div>
      </Show>
    </div>
  );
}
